import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, MessageSquare, Globe, Cpu, MoreHorizontal, Send, Check, AlertCircle, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'discord',      label: 'Discord',          desc: 'Bot, tickets, channels, rôles…',         icon: MessageSquare, color: 'indigo' },
  { id: 'website',      label: 'Site web',         desc: 'UI/UX, pages, fonctionnalités…',         icon: Globe,         color: 'cyan' },
  { id: 'optimization', label: 'Optimisations PC', desc: 'Nouveaux tweaks, jeux, profils…',        icon: Cpu,           color: 'emerald' },
  { id: 'other',        label: 'Autre',            desc: 'Idée globale, partenariat, contenu…',    icon: MoreHorizontal, color: 'purple' },
];

const ACCENT = {
  indigo:  { brd: 'border-indigo-500/40',  bg: 'bg-indigo-500/10',  tx: 'text-indigo-300',  ring: 'ring-indigo-500/30',  glow: 'shadow-[0_0_30px_-8px_rgba(99,102,241,0.5)]' },
  cyan:    { brd: 'border-cyan-500/40',    bg: 'bg-cyan-500/10',    tx: 'text-cyan-300',    ring: 'ring-cyan-500/30',    glow: 'shadow-[0_0_30px_-8px_rgba(6,182,212,0.5)]' },
  emerald: { brd: 'border-emerald-500/40', bg: 'bg-emerald-500/10', tx: 'text-emerald-300', ring: 'ring-emerald-500/30', glow: 'shadow-[0_0_30px_-8px_rgba(16,185,129,0.5)]' },
  purple:  { brd: 'border-purple-500/40',  bg: 'bg-purple-500/10',  tx: 'text-purple-300',  ring: 'ring-purple-500/30',  glow: 'shadow-[0_0_30px_-8px_rgba(168,85,247,0.5)]' },
};

const SuggestionsPage = () => {
  const { isAuthenticated, token, loginWithDiscord } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const submit = async () => {
    setError(null);
    if (!category) { setError('Choisis une catégorie.'); return; }
    if (title.trim().length < 3) { setError('Le titre doit faire au moins 3 caractères.'); return; }
    if (message.trim().length < 10) { setError('Décris ton idée un peu plus (min 10 caractères).'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category, title: title.trim(), message: message.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Erreur lors de l\'envoi.');
      }
      setSuccess(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
        <div data-testid="suggestion-success" className="max-w-md w-full bg-emerald-500/10 border border-emerald-500/40 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <Check className="w-7 h-7 text-emerald-300" />
          </div>
          <h2 className="text-2xl font-bold text-white">Merci pour ton idée !</h2>
          <p className="text-gray-400 text-sm mt-2 leading-relaxed">
            Ta suggestion a bien été envoyée. L'équipe va la lire et y répondre dès que possible.
          </p>
          <div className="flex gap-2 justify-center mt-6">
            <button
              onClick={() => { setSuccess(false); setCategory(null); setTitle(''); setMessage(''); }}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Envoyer une autre
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 md:pt-28 pb-16 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold uppercase tracking-[0.18em] mb-4">
            <Zap className="w-3.5 h-3.5" />
            Communauté
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            Une idée ?<br />
            <span className="bg-gradient-to-r from-amber-300 via-orange-300 to-amber-400 bg-clip-text text-transparent">Partage-la avec nous.</span>
          </h1>
          <p className="text-gray-400 text-base md:text-lg mt-5 max-w-xl mx-auto leading-relaxed">
            Une amélioration sur le Discord, le site, une optimisation que tu rêves de voir ?
            Toute idée compte. Les meilleures sont mises en place.
          </p>
        </div>

        {!isAuthenticated ? (
          <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-2xl p-8 text-center">
            <Lightbulb className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
            <p className="text-white font-semibold text-lg">Connecte-toi pour suggérer une idée</p>
            <p className="text-gray-400 text-sm mt-2">Avec ton compte Discord, en 2 secondes.</p>
            <button
              onClick={loginWithDiscord}
              data-testid="suggestion-login-btn"
              className="mt-5 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl transition-colors"
            >
              Se connecter avec Discord
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Step 1: Pick a category */}
            <div>
              <h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-[0.18em] mb-3">1 · Catégorie</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {CATEGORIES.map(({ id, label, desc, icon: Icon, color }) => {
                  const a = ACCENT[color];
                  const active = category === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setCategory(id)}
                      data-testid={`suggestion-cat-${id}`}
                      className={`text-left p-4 rounded-xl border transition-all duration-200 ${
                        active
                          ? `${a.brd} ${a.bg} ${a.glow} ring-2 ${a.ring}`
                          : 'border-white/10 bg-zinc-900/40 hover:border-white/20 hover:bg-zinc-900/60'
                      }`}
                    >
                      <Icon className={`w-5 h-5 mb-2 ${active ? a.tx : 'text-gray-400'} transition-colors`} />
                      <p className={`font-semibold text-sm ${active ? 'text-white' : 'text-gray-300'}`}>{label}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Title */}
            <div>
              <h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-[0.18em] mb-3">2 · Titre</h2>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                placeholder="Ex : Ajouter un bouton 'Mode soirée' sur le site"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/60 focus:bg-black/60 transition-all"
                data-testid="suggestion-title-input"
              />
              <div className="flex justify-between mt-1.5">
                <p className="text-[11px] text-gray-500">Sois clair et concis.</p>
                <p className="text-[11px] text-gray-500 tabular-nums">{title.length} / 120</p>
              </div>
            </div>

            {/* Step 3: Message */}
            <div>
              <h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-[0.18em] mb-3">3 · Décris ton idée</h2>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
                rows={5}
                placeholder="Explique en détail : à quoi ça sert, comment tu vois la chose, des exemples si tu en as..."
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/60 focus:bg-black/60 transition-all resize-none"
                data-testid="suggestion-message-input"
              />
              <div className="flex justify-between mt-1.5">
                <p className="text-[11px] text-gray-500">Plus c'est détaillé, plus c'est facile à mettre en place.</p>
                <p className="text-[11px] text-gray-500 tabular-nums">{message.length} / 2000</p>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-xl">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <button
              onClick={submit}
              disabled={submitting}
              data-testid="suggestion-submit-btn"
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all shadow-[0_10px_30px_-8px_rgba(251,146,60,0.55)] hover:shadow-[0_14px_40px_-6px_rgba(251,146,60,0.75)]"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Envoi en cours...' : 'Envoyer ma suggestion'}
            </button>

            <p className="text-center text-[11px] text-gray-600">
              Limite : 5 suggestions par 24h. On lit toutes les idées qui rentrent.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuggestionsPage;
